/* eslint-disable react-refresh/only-export-components */
import React, {
  forwardRef,
  memo,
  useId,
  type ComponentType,
  type ReactNode,
} from "react";
import { Input, InputNumber, Switch } from "antd";
import type { InputProps } from "antd";
import type { InputNumberProps } from "antd";
import type { SwitchProps } from "antd";
import type { PasswordProps, TextAreaProps } from "antd/es/input";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import "./CustomInputWithLabel.css";

const { Password, TextArea } = Input;

type InputComponentType =
  | "input"
  | "password"
  | "textarea"
  | "switch"
  | "number"
  | ComponentType<Record<string, unknown>>;

export interface CustomInputWithLabelProps
  extends Omit<InputProps, "children"> {
  label?: ReactNode;
  showLabel?: boolean;
  labelPosition?: "top" | "left";
  inputComponent?: InputComponentType;
  containerClassName?: string;
  labelClassName?: string;
  wrapperClassName?: string;
  showColon?: boolean;
  children?: ReactNode;
  required?: boolean;
}

const CustomInputWithLabel = forwardRef<HTMLElement, CustomInputWithLabelProps>(
  (
    {
      label,
      showLabel = true,
      labelPosition = "top",
      inputComponent = "input",
      className = "",
      containerClassName = "",
      labelClassName = "",
      wrapperClassName = "",
      showColon = true,
      id,
      children,
      required = false,
      ...restProps
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const translatedLabel = typeof label === "string" ? t(label) : label;

    const shouldShowLabel =
      showLabel && translatedLabel !== undefined && translatedLabel !== null;

    const effectiveLabelPosition =
      inputComponent === "switch" && labelPosition === "top"
        ? "left"
        : labelPosition;

    const containerClasses =
      effectiveLabelPosition === "left"
        ? twMerge(
            "custom-input-with-label flex flex-row items-center gap-2",
            containerClassName,
          )
        : twMerge(
            "custom-input-with-label flex flex-col gap-2",
            containerClassName,
          );

    const defaultLabelClass =
      "text-[14px] leading-[18px] text-[var(--color-gray9)] font-normal";
    const positionClass =
      effectiveLabelPosition === "left" ? "flex items-center" : "block";
    const labelClasses = twMerge(
      defaultLabelClass,
      positionClass,
      labelClassName,
    );

    const renderRequiredIndicator = () =>
      required ? <span className="ml-1 text-[#FF4D4F]">*</span> : null;

    const getLabelContent = () => {
      if (!shouldShowLabel) return null;

      if (typeof translatedLabel === "string") {
        const trimmed = translatedLabel.trim();
        const hasColon = trimmed.endsWith(":");
        const labelText =
          showColon && hasColon ? trimmed.slice(0, -1) : trimmed;
        const labelBody = (
          <>
            {labelText}
            {renderRequiredIndicator()}
          </>
        );

        if (!showColon) return labelBody;
        return (
          <>
            {labelBody}
            <span>:</span>
          </>
        );
      }

      const labelBody = (
        <>
          {translatedLabel}
          {renderRequiredIndicator()}
        </>
      );

      if (!showColon) return labelBody;
      return (
        <>
          {labelBody}
          <span>:</span>
        </>
      );
    };

    const renderInputComponent = () => {
      const inputProps = {
        ...restProps,
        ref,
        id: inputId,
        required,
        className: twMerge(
          "text-[14px] leading-[18px]",
          effectiveLabelPosition === "left" ? "" : "w-full",
          className,
        ),
      };

      switch (inputComponent) {
        case "password":
          return <Password {...(inputProps as PasswordProps)} />;

        case "textarea":
          return <TextArea {...(inputProps as TextAreaProps)} />;

        case "switch":
          return (
            <Switch
              {...(restProps as SwitchProps)}
              ref={ref as React.Ref<HTMLButtonElement>}
              id={inputId}
            />
          );

        case "number":
          return (
            <InputNumber {...(inputProps as InputNumberProps)} />
          );

        case "input":
          return <Input {...inputProps} />;

        default: {
          if (typeof inputComponent === "string") {
            return <Input {...inputProps} />;
          }

          const CustomComponent = inputComponent;
          return (
            <CustomComponent
              {...inputProps}
              {...(children ? { children } : {})}
            />
          );
        }
      }
    };

    if (effectiveLabelPosition === "left") {
      const leftWrapperClasses = twMerge(
        "flex items-center gap-2",
        wrapperClassName,
      );

      return (
        <div className={containerClasses}>
          {shouldShowLabel && (
            <label className={labelClasses} htmlFor={inputId}>
              {getLabelContent()}
            </label>
          )}
          <div className={leftWrapperClasses}>{renderInputComponent()}</div>
        </div>
      );
    }

    return (
      <div className={containerClasses}>
        {shouldShowLabel && (
          <label className={labelClasses} htmlFor={inputId}>
            {getLabelContent()}
          </label>
        )}
        {renderInputComponent()}
      </div>
    );
  },
);

CustomInputWithLabel.displayName = "CustomInputWithLabel";

export default memo(CustomInputWithLabel);
